import http from "@/api/request";
import { SYSTEM_OPS_BASE, SYSTEM_SERVICE_BASE } from "@/config/apiBaseUrlConfig";

import type {
  GetAssignableMenuGroupsParams,
  GetEmployeeRolesParams,
  GetOrgRoleGroupsParams,
  GetRolesParams,
  GetTemplateRolesParams,
  GetUserRolesParams,
  OrgRoleGroupVO,
  PublicRoleLibraryStatsVO,
  RoleBatchCreateRequest,
  RoleBatchIdsRequest,
  RoleBatchListItemVO,
  RoleBatchMenuSaveRequest,
  RoleBatchStatusUpdateRequest,
  RoleDetailVO,
  RoleListVO,
  RoleMenuGroupVO,
  RoleStatusUpdateRequest,
  RoleUpdateRequest,
  UserRoleBatchStatusUpdateRequest,
  UserRoleInfoVO,
  UserRoleVO,
} from "./type";

/** 角色模块 API 前缀（system） */
const ROLE_BASE = `${SYSTEM_SERVICE_BASE}/role`;
/** 运营端角色模块 API 前缀（ops） */
const OPS_ROLE_BASE = `${SYSTEM_OPS_BASE}/role`;

/**
 * 可选菜单分组树：按 org 返回各 system_type 菜单树
 * GET /service-chijing-system/api/system/role/getAssignableMenuGroups
 * @param params.orgId 组织 ID，用于过滤 org 可用 systemType（int64）
 * @param params.orgType 组织类型：SUPPLIER / AGENT / STORE
 * @param params.roleId 角色 ID，编辑时传入以限定 systemTypeId（int64）
 */
export const getAssignableMenuGroups = (params?: GetAssignableMenuGroupsParams) => {
  return http.get<RoleMenuGroupVO[]>(`${ROLE_BASE}/getAssignableMenuGroups`, { params });
};

/**
 * 通用角色列表查询
 * GET /service-chijing-system/api/system/role/getRoles
 * @param params 筛选条件
 */
export const getRoles = (params?: GetRolesParams) => {
  return http.get<RoleListVO[]>(`${ROLE_BASE}/getRoles`, { params });
};

/**
 * 按 ID 查询角色详情及已选 menuIds
 * GET /service-chijing-system/api/system/role/getRoleDetail/{id}
 * @param id 角色 ID
 */
export const getRoleDetail = (id: string) => {
  return http.get<RoleDetailVO>(`${ROLE_BASE}/getRoleDetail/${id}`);
};

/**
 * 批量查询角色
 * POST /service-chijing-system/api/system/role/listRolesByIds
 * 供跨服务按 roleIds 校验/加载角色
 * @param data 角色 ID 列表
 */
export const listRolesByIds = (data: RoleBatchIdsRequest) => {
  return http.post<RoleBatchListItemVO[]>(`${ROLE_BASE}/listRolesByIds`, data);
};

/**
 * 组织分组角色列表：点击某 org 时调用
 * GET /service-chijing-system/api/system/role/getOrgRoleGroups
 * templateRoles=该 org 类型公用模板，orgRoles=该 org 下自建 ORG 角色
 * @param params.orgId 组织 ID，必填
 */
export const getOrgRoleGroups = (params: GetOrgRoleGroupsParams) => {
  return http.get<OrgRoleGroupVO>(`${ROLE_BASE}/getOrgRoleGroups`, { params });
};

/**
 * 批量新增角色
 * POST /service-chijing-system/api/system/role/addRoles
 * 一次提交多端建角色，同一事务
 * @param data 批量创建参数
 */
export const addRoles = (data: RoleBatchCreateRequest) => {
  return http.post<string[]>(`${ROLE_BASE}/addRoles`, data);
};

/**
 * 启用/禁用角色
 * PUT /service-chijing-system/api/system/role/updateRoleStatus/{id}
 * DISABLE 后禁止新绑员工，已绑保留但权限失效
 * @param id 角色 ID
 * @param data 状态变更参数
 */
export const updateRoleStatus = (id: string, data: RoleStatusUpdateRequest) => {
  return http.put<void>(`${ROLE_BASE}/updateRoleStatus/${id}`, data);
};

/**
 * 批量启用/禁用角色
 * PUT /service-chijing-system/api/system/role/batchUpdateRoleStatus
 * DISABLE 后禁止新绑员工，已绑保留但权限失效
 * @param data 角色 ID 列表与目标状态
 */
export const batchUpdateRoleStatus = (data: RoleBatchStatusUpdateRequest) => {
  return http.put<void>(`${ROLE_BASE}/batchUpdateRoleStatus`, data);
};

/**
 * 批量启用/禁用用户角色绑定
 * PUT /service-chijing-system/api/system/role/batchUpdateUserRoleStatus
 * 按 userId + roleIds 切换绑定状态，不改角色主数据
 * @param data 用户、角色 ID 列表与绑定目标状态
 */
export const batchUpdateUserRoleStatus = (data: UserRoleBatchStatusUpdateRequest) => {
  return http.put<void>(`${ROLE_BASE}/batchUpdateUserRoleStatus`, data);
};

/**
 * 更新单条角色
 * PUT /service-chijing-system/api/system/role/updateRole/{id}
 * 不可改 roleType / systemTypeId / orgId / status
 * @param id 角色 ID
 * @param data 角色名称、描述及更新人
 */
export const updateRole = (id: string, data: RoleUpdateRequest) => {
  return http.put<void>(`${ROLE_BASE}/updateRole/${id}`, data);
};

/**
 * 批量保存角色菜单（按 items 全量覆盖 role_menu，同一事务）
 * PUT /service-chijing-system/api/system/role/batchSaveRoleMenus
 * @param data 角色菜单保存明细列表
 */
export const batchSaveRoleMenus = (data: RoleBatchMenuSaveRequest) => {
  return http.put<void>(`${ROLE_BASE}/batchSaveRoleMenus`, data);
};

/**
 * 模板类型角色列表：点击角色模板（SUPPLIER/AGENT/STORE）时调用
 * GET /service-chijing-system/api/system/role/getTemplateRoles
 * @param params.templateType 模板类型，必填
 */
export const getTemplateRoles = (params: GetTemplateRolesParams) => {
  return http.get<RoleListVO[]>(`${ROLE_BASE}/getTemplateRoles`, { params });
};

/**
 * 公有角色库分类统计
 * GET /service-chijing-system/api/ops/role/getPublicRoleLibraryStats
 * 返回供应商/代理商/门店三类 TEMPLATE 角色数量，供左侧导航展示
 */
export const getPublicRoleLibraryStats = () => {
  return http.get<PublicRoleLibraryStatsVO>(`${OPS_ROLE_BASE}/getPublicRoleLibraryStats`);
};

/**
 * 查询员工已绑角色
 * GET /service-chijing-system/api/system/role/getEmployeeRoles
 * 按 employeeId + orgId 查询
 * @param params.employeeId 员工 ID（int64）
 * @param params.orgId 组织 ID（int64）
 */
export const getEmployeeRoles = (params: GetEmployeeRolesParams) => {
  return http.get<UserRoleInfoVO[]>(`${ROLE_BASE}/getEmployeeRoles`, { params });
};

/**
 * 查询用户已绑角色
 * GET /service-chijing-system/api/system/role/getUserRoles
 * 按 user_id 查询绑定记录，按 org_id + role_name 分组返回
 * @param params.userId 用户 ID
 */
export const getUserRoles = (params: GetUserRolesParams) => {
  return http.get<UserRoleVO[]>(`${ROLE_BASE}/getUserRoles`, {
    params: { user_id: params.userId },
  });
};
